import { useDisclosure } from '@chakra-ui/react';

import { useAsyncExportFlow } from '../../../hooks/useAsyncExportFlow';
import { AsyncJobProcessTypeEnum } from '../../../types/asyncExport.types';
import { ExportConfirmationModal } from '../../modal/ExportConfirmationModal';
import { ExportOngoingModal } from '../../modal/ExportOngoingModal';
import { RecentActivityLogModal } from '../../modal/RecentActivityLogModal';

interface AsyncExportButtonProps {
	processType: AsyncJobProcessTypeEnum;
	cycleId?: number;
	companyAreaId?: number;
	collaborationType?: 'planning' | 'marketing' | 'commercial';
	onClick: () => void;
	isDisabled?: boolean;
	isLoading?: boolean;
	children: React.ReactNode;
	onExportStarted?: () => void;
}

export const AsyncExportButton = ({
	processType,
	cycleId,
	companyAreaId,
	collaborationType,
	onClick,
	isDisabled = false,
	isLoading = false,
	children,
	onExportStarted,
}: AsyncExportButtonProps) => {
	const {
		isOpen: isActivityModalOpen,
		onOpen: onActivityModalOpen,
		onClose: onActivityModalClose,
	} = useDisclosure();

	const handleExportStarted = (): void => {
		onActivityModalOpen();
		if (onExportStarted) {
			onExportStarted();
		}
	};

	const {
		handleExportClick,
		isLoading: isExporting,
		showConfirmationModal,
		showOngoingModal,
		handleConfirmation,
		handleConfirmationCancel,
		handleOngoingConfirm,
		handleOngoingCancel,
	} = useAsyncExportFlow({
		processType,
		cycleId,
		companyAreaId,
		collaborationType,
		onSuccess: onClick,
		onExportStarted: handleExportStarted,
	});

	const handleClick = (): void => {
		if (!isDisabled && !isLoading && !isExporting) {
			handleExportClick();
		}
	};

	return (
		<>
			<div
				onClick={handleClick}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						handleClick();
					}
				}}
				role='button'
				tabIndex={isDisabled || isLoading || isExporting ? -1 : 0}
				style={{
					cursor:
						isDisabled || isLoading || isExporting
							? 'not-allowed'
							: 'pointer',
					opacity: isDisabled || isLoading || isExporting ? 0.6 : 1,
				}}
			>
				{children}
			</div>
			<ExportConfirmationModal
				isOpen={showConfirmationModal}
				onConfirm={handleConfirmation}
				onCancel={handleConfirmationCancel}
			/>
			<ExportOngoingModal
				isOpen={showOngoingModal}
				onConfirm={handleOngoingConfirm}
				onCancel={handleOngoingCancel}
			/>
			<RecentActivityLogModal
				isOpen={isActivityModalOpen}
				onClose={onActivityModalClose}
			/>
		</>
	);
};
